import React, { useState, useEffect } from 'react';
import { 
  Clock, User, FileText, ChevronLeft, ChevronRight, 
  Filter, RefreshCw, Activity, Loader2, Search
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { API_URL } from '../../utils/api';

// Action type display mapping
const ACTION_LABELS = {
  'create_booking': { label: 'Created Booking', color: 'bg-green-100 text-green-700' },
  'create_manual_booking': { label: 'Created Manual Booking', color: 'bg-green-100 text-green-700' },
  'update_booking': { label: 'Updated Booking', color: 'bg-blue-100 text-blue-700' },
  'delete_booking': { label: 'Deleted Booking', color: 'bg-red-100 text-red-700' },
  'add_payment': { label: 'Added Payment', color: 'bg-emerald-100 text-emerald-700' },
  'change_status': { label: 'Changed Status', color: 'bg-amber-100 text-amber-700' },
  'resend_confirmation': { label: 'Resent Confirmation', color: 'bg-purple-100 text-purple-700' },
  'send_invoice': { label: 'Sent Invoice', color: 'bg-indigo-100 text-indigo-700' },
  'payment_plan_created': { label: 'Created Payment Plan', color: 'bg-teal-100 text-teal-700' },
  'payment_plan_cancelled': { label: 'Cancelled Payment Plan', color: 'bg-rose-100 text-rose-700' },
};

const getActionDisplay = (action) => {
  return ACTION_LABELS[action] || { label: action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), color: 'bg-slate-100 text-slate-700' };
};

const formatDateTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatChanges = (changes) => {
  if (!changes || Object.keys(changes).length === 0) return null;
  
  return (
    <div className="mt-2 text-xs bg-slate-50 rounded p-2 space-y-1">
      {Object.entries(changes).map(([field, { old: oldVal, new: newVal }]) => (
        <div key={field} className="flex flex-wrap gap-1">
          <span className="font-medium text-slate-600">{field}:</span>
          {oldVal !== undefined && oldVal !== null && (
            <span className="text-red-600 line-through">{JSON.stringify(oldVal)}</span>
          )}
          <span className="text-slate-500">→</span>
          <span className="text-green-600">{JSON.stringify(newVal)}</span>
        </div>
      ))}
    </div>
  );
};

export function AdminAuditTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(25);
  
  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [daysFilter, setDaysFilter] = useState('7');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Summary
  const [summary, setSummary] = useState(null);
  const [actionTypes, setActionTypes] = useState([]);

  useEffect(() => {
    fetchLogs();
    fetchSummary();
    fetchActionTypes();
  }, [offset, actionFilter, userFilter, daysFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });
      
      if (actionFilter) params.append('action', actionFilter);
      if (userFilter) params.append('user_email', userFilter);
      if (daysFilter) params.append('days', daysFilter);
      
      const response = await fetch(`${API_URL}/api/admin/audit?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      } else {
        toast.error('Failed to load audit logs');
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Error loading audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/audit/summary?days=${daysFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchActionTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/audit/actions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActionTypes(data.actions || []);
      }
    } catch (error) {
      console.error('Error fetching action types:', error);
    }
  };

  const handleRefresh = () => {
    setOffset(0);
    fetchLogs();
    fetchSummary();
  };

  const filteredLogs = searchQuery 
    ? logs.filter(log => 
        log.target_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.target_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : logs;

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-6" data-testid="audit-log-tab">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.total_actions}</p>
                <p className="text-sm text-slate-500">Total Actions ({daysFilter}d)</p>
              </div>
            </div>
          </div>
          
          {summary.by_user?.slice(0, 3).map((user, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-lg font-semibold text-slate-900">{user.count}</p>
                  <p className="text-sm text-slate-500 truncate" title={user.email}>{user.email.split('@')[0]}</p>
                  <Badge variant="outline" className="text-xs mt-1">{user.role}</Badge>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Filters:</span>
          </div>
          
          <Select value={daysFilter} onValueChange={(v) => { setDaysFilter(v); setOffset(0); }}>
            <SelectTrigger className="w-[130px]" data-testid="days-filter">
              <SelectValue placeholder="Time period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={actionFilter || "all"} onValueChange={(v) => { setActionFilter(v === "all" ? "" : v); setOffset(0); }}>
            <SelectTrigger className="w-[180px]" data-testid="action-filter">
              <SelectValue placeholder="All actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {actionTypes.map(action => (
                <SelectItem key={action} value={action}>{getActionDisplay(action).label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search by name, ID, or user..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="audit-search"
            />
          </div>
          
          <Button variant="outline" size="sm" onClick={handleRefresh} data-testid="refresh-audit">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>No audit logs found</p>
            <p className="text-sm mt-1">Activity will appear here as actions are performed</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Time</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">User</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Action</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Target</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLogs.map((log) => {
                    const actionDisplay = getActionDisplay(log.action);
                    return (
                      <tr key={log.audit_id} className="hover:bg-slate-50" data-testid={`audit-row-${log.audit_id}`}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Clock className="w-4 h-4 text-slate-500" />
                            {formatDateTime(log.timestamp)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate-900">{log.user_email}</div>
                          <Badge variant="outline" className="text-xs mt-1">{log.user_role}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={actionDisplay.color}>{actionDisplay.label}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate-900">{log.target_name || '-'}</div>
                          <div className="text-xs text-slate-500 font-mono">{log.target_id}</div>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          {log.details && Object.keys(log.details).length > 0 && (
                            <div className="text-xs text-slate-600">
                              {log.details.amount && <span>£{log.details.amount.toFixed(2)}</span>}
                              {log.details.method && <span> via {log.details.method}</span>}
                              {log.details.sent_to && <span>Sent to: {log.details.sent_to}</span>}
                              {log.details.event_title && !log.details.amount && (
                                <span>{log.details.event_title}</span>
                              )}
                            </div>
                          )}
                          {formatChanges(log.changes)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
              <div className="text-sm text-slate-600">
                Showing {offset + 1} - {Math.min(offset + limit, total)} of {total} entries
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  data-testid="prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                  data-testid="next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
